// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductActions from "@adl/App/Components/Product/ProductActions";
import ProductGridLayout, { ProductGridLayoutStyle } from "@adl/App/Components/Product/ProductGridLayout";
import ProductPreviousPurchase from "@adl/App/Components/Product/ProductPreviousPurchase";
import productDescriptionStyles from "@adl/App/SharedStyle/ProductDescriptionTextStyles";
import { xProductPreviousPurchaseStyle } from "@adl/App/SharedStyle/productPreviousPurchaseSharedStyles";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@adl/Overrides/Components/ProductPartNumbers";
import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import ProductListProductImage from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import Badge from "@insite/mobius/Badge";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
});

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    recommendationId?: number;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    extendedStyles?: ProductCarouselProductStyles;
    // XNG-Changes
    showInventory?: boolean;
    showCompare?: boolean;
    actionsTemplate?: (product: ProductModel) => React.ReactNode;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    // XNG-Changes
    wrapper?: InjectableCss;
    partNumbersGridItem?: InjectableCss;
    productGridLayoutStyle?: ProductGridLayoutStyle;
    productPriceStyles?: ProductPriceStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    addToCartWrapper?: InjectableCss;
    addToListWrapper?: InjectableCss;
    compareAddToListWrapper?: InjectableCss;
    productPreviousPurchase?: TypographyPresentationProps;
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
        `,
    },
    productBrandStyles: {
        nameLink: {
            css: css`
                margin-bottom: 8px;
            `,
        },
    },
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                justify-content: center;
            `,
        },
        img: {
            css: css`
                max-width: 100%;
                max-height: 100%;
            `,
        },
    },
    productDescriptionStyles: {
        ...productDescriptionStyles("large", {
            productDetailLink: {
                css: css`
                    display: block;
                    margin-bottom: 12px;
                    min-height: 50px;
                `,
            },
        }),
    },
    partNumbersGridItem: {
        css: css`
            display: flex;
        `,
    },
    productPartNumbersStyles: {
        container: {
            gap: 4,
            css: css`
                width: 50%;
            `,
        },
    },
    productPriceStyles: {
        wrapper: {
            css: css`
                text-align: left;
            `,
        },
        price: {
            priceText: {
                size: 18,
                weight: "bold",
                css: css`
                    line-height: 22px;
                `,
            },
        },
    },
    productQuantityBreakPricingStyles: {
        viewLink: {
            css: css`
                margin: 8px 0;
            `,
        },
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 20px;
            `,
        },
    },
    productAvailabilityStyles: {
        container: {
            css: css`
                display: flex;
                flex-direction: column;
            `,
        },
    },
    addToCartWrapper: {
        css: css`
            width: 100%;
            display: flex;
            align-items: flex-end;
            margin: 0;
            justify-content: space-between;
        `,
    },
    productQuantityOrderedStyles: {
        mergeCss: true,
        cssOverrides: {
            formField: css`
                margin-right: 10px;
                width: 100px;
            `,
        },
    },
    productAddToCartButtonStyles: {
        css: css`
            flex-grow: 1;
            margin-left: 12px;
        `,
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
    compareAddToListWrapper: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: ${({ showCompare }: any) => (showCompare ? "space-between" : "flex-end")};
            div[data-test-selector="productList_CompareCheckbox"] {
                padding-top: 12px;
            }
        `,
    },
    productPreviousPurchase: xProductPreviousPurchaseStyle,
};

const ProductCarouselProduct = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showInventory,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
    showCompare,
    actionsTemplate,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCarouselProductStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });
    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    return (
        <>
            <ProductContext.Provider value={productContext}>
                <ProductGridLayout
                    id={`product_${product.id}`}
                    data-test-selector="product-carousel-item"
                    extendedStyles={styles.productGridLayoutStyle}
                >
                    <ProductGridLayout.Image>
                        {showImage && !showOverlayBadge && (
                            <ProductImage
                                product={productContext}
                                extendedStyles={styles.productImageStyles}
                                onClick={() => trackProductRecommendation(true)}
                                {...{ transformations: { width: 287 } }}
                            />
                        )}
                        {showImage && showOverlayBadge && overlayBadge && (
                            <StyledWrapper
                                {...styles.badgeAreaWrapper}
                                ref={badgeAreaWrapperRef}
                                data-test-selector="productCarouselBadge"
                            >
                                <ProductImage extendedStyles={styles.productImageStyles} product={productContext} />
                                <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                                    <Badge
                                        type={overlayBadge.badgeType}
                                        value={
                                            overlayBadge.badgeType === "Image"
                                                ? overlayBadge.largeImageBadgePath
                                                : overlayBadge.displayText
                                        }
                                        badgeTextColor={overlayBadge.textColorHexCode}
                                        badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                        badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                        imageAltText={
                                            overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name
                                        }
                                        textBadgeCss={styles.badgeText}
                                        imageBadgeProps={styles.badgeImageProps}
                                    />
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                    </ProductGridLayout.Image>
                    <ProductGridLayout.Info>
                        {showBrand && product.brand && (
                            <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                        )}
                        {showTitle && (
                            <ProductDescription
                                product={productContext}
                                extendedStyles={styles.productDescriptionStyles}
                                onClick={() => trackProductRecommendation(true)}
                            />
                        )}
                        <StyledWrapper {...styles.partNumbersGridItem}>
                            {showPartNumbers && (
                                <ProductPartNumbers
                                    productNumber={product.productNumber}
                                    customerProductNumber={product.customerProductNumber}
                                    manufacturerItem={product.manufacturerItem}
                                    brand={product.brand}
                                    showCustomerName={false}
                                    showManufacturerItem={true}
                                    extendedStyles={styles.productPartNumbersStyles}
                                />
                            )}
                            {showTextBadges && textBadges && (
                                <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />
                            )}
                            {showImageBadges && imageBadges && (
                                <ProductImageBadges
                                    badgeList={imageBadges}
                                    maxBadges={maximumNumberOfImageBadges ?? 4}
                                />
                            )}
                            <ProductPreviousPurchase
                                product={product}
                                extendedStyles={styles.productPreviousPurchase}
                            />
                        </StyledWrapper>
                    </ProductGridLayout.Info>
                    <ProductGridLayout.PriceAndActionsSection>
                        <ProductGridLayout.PriceSection>
                            {showPrice && (
                                <StyledWrapper {...styles.productPriceWrapper}>
                                    <VisuallyHidden
                                        as="label"
                                        htmlFor={`${carouselId}-uom`}
                                        id={`${carouselId}-uom-label`}
                                    >
                                        {translate("U/M")}
                                    </VisuallyHidden>
                                    <ProductPrice
                                        showLabel={false}
                                        product={productContext}
                                        extendedStyles={styles.productPriceStyles}
                                    />
                                    {product.unitOfMeasures && (
                                        <ProductUnitOfMeasureSelect
                                            labelOverride=""
                                            extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                            id={`${carouselId}-uom`}
                                        />
                                    )}
                                    <ProductQuantityBreakPricing
                                        extendedStyles={styles.productQuantityBreakPricingStyles}
                                    />
                                </StyledWrapper>
                            )}
                        </ProductGridLayout.PriceSection>
                        <ProductGridLayout.AvailabilitySection>
                            {showInventory && (
                                <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                            )}
                        </ProductGridLayout.AvailabilitySection>
                        <ProductGridLayout.AddToCartSection>
                            {showAddToCart && (
                                <StyledWrapper {...styles.addToCartWrapper}>
                                    <ProductActions onAddToCartClick={() => trackProductRecommendation(false)} />
                                </StyledWrapper>
                            )}
                        </ProductGridLayout.AddToCartSection>
                        <ProductGridLayout.AddToListAction>
                            <StyledWrapper {...styles.compareAddToListWrapper} {...{ showCompare }}>
                                {showCompare && (
                                    <StyledWrapper>
                                        <ProductListProductImage showImage={false} showCompare={showCompare} />
                                    </StyledWrapper>
                                )}

                                {showAddToList && (
                                    <StyledWrapper {...styles.addToListWrapper}>
                                        <ProductAddToListLink
                                            data-test-selector="addToListLink"
                                            extendedStyles={styles.productAddToListLinkStyles}
                                        />
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                            {actionsTemplate ? actionsTemplate(product) : []}
                        </ProductGridLayout.AddToListAction>
                    </ProductGridLayout.PriceAndActionsSection>
                </ProductGridLayout>
            </ProductContext.Provider>
        </>
    );
};

ProductCarouselProduct.defaultProps = {
    showInventory: true,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
